/**
 * Fixture mirror of the provisioning-template doors (G-PRV-05 console, 2026-11-04).
 *
 * The server's read door (`GET /ops/v1/templates`, routers/system.py) is a passthrough
 * of the NBI's preset list and its write door (`PUT /ops/v1/templates/{name}`) echoes the
 * document verbatim with NO created/replaced field — a 2xx only says the NBI took it.
 * The mirror ports those DOORS, not GenieACS: upsert stores the document the caller
 * wrote and echoes it back; nothing simulates a CPE applying a preset, because no CPE
 * exists in the fixture world (D4: the fixture invents nothing about devices).
 *
 * The refusal sentences are the server's own words (the 422 structural guards of
 * routers/system.py::put_template), rendered the same way the live door renders them.
 */

export interface FixturePreset {
  name: string;
  document: Record<string, unknown>;
}

/** The 422 wording for a name that does not match the document's own (server: field `name`). */
export function templateNameMismatch(): string {
  return 'the document does not carry this name';
}

/** The 422 wording for an empty actions array (server: structural guard). */
export function templateActionsEmpty(): string {
  return 'the document declares no actions';
}

/** The seed documents — three shapes an FTTX operator actually writes (basic data / wifi / bridge). */
export const FIXTURE_TEMPLATES: readonly FixturePreset[] = [
  {
    name: 'pppoe-basic',
    document: {
      name: 'pppoe-basic',
      schedule: 'now',
      events: ['0 BOOTSTRAP', '2 PERIODIC'],
      actions: [
        {
          type: 'value',
          name: 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.Username',
          value: 'fttx-${SerialNumber}',
        },
        {
          type: 'value',
          name: 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANPPPConnection.1.Password',
          value: 'RESET',
        },
      ],
    },
  },
  {
    name: 'wifi-defaults',
    document: {
      name: 'wifi-defaults',
      schedule: 'now',
      events: ['2 PERIODIC'],
      actions: [
        {
          type: 'value',
          name: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.SSID',
          value: 'WiNet-${SerialNumber}',
        },
        {
          type: 'value',
          name: 'InternetGatewayDevice.LANDevice.1.WLANConfiguration.1.KeyPassphrase',
          value: 'RESET',
        },
      ],
    },
  },
  {
    name: 'bridge-mode',
    document: {
      name: 'bridge-mode',
      schedule: 'now',
      events: ['0 BOOTSTRAP'],
      actions: [
        {
          type: 'value',
          name: 'InternetGatewayDevice.WANDevice.1.WANConnectionDevice.1.WANIPConnection.1.ConnectionType',
          value: 'IP_Routed',
        },
      ],
    },
  },
];

/** The room the doors share — cloned per fixture session by `createFixtureApi`. */
export class TemplateStore {
  private readonly byName: Map<string, Record<string, unknown>>;

  constructor(seed: readonly FixturePreset[] = FIXTURE_TEMPLATES) {
    this.byName = new Map(seed.map((p) => [p.name, p.document]));
  }

  list(): { items: Record<string, unknown>[]; total: number; note: null } {
    return { items: [...this.byName.values()], total: this.byName.size, note: null };
  }

  /** The PUT door's contract: store verbatim, echo verbatim, no created/replaced. */
  upsert(
    name: string,
    document: Record<string, unknown>,
  ): { name: string; document: Record<string, unknown>; note: null } {
    this.byName.set(name, document);
    return { name, document, note: null };
  }
}
